import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Download, FileSpreadsheet } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { formatPhoneNumber } from '@/lib/utils';

interface QuickReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Period = '7d' | '1m' | '3m' | '12m';

const PERIOD_LABELS: Record<Period, string> = {
  '7d': 'Last 7 Days',
  '1m': 'Last 1 Month',
  '3m': 'Last 3 Months',
  '12m': 'Last 12 Months',
};

interface DIDUsageRow {
  did: string;
  inbound_calls: number;
  inbound_minutes: number;
  outbound_calls: number;
  outbound_minutes: number;
  total_calls: number;
  total_minutes: number;
}

interface UsageReportData {
  period: string;
  start_date: string;
  end_date: string;
  dids: DIDUsageRow[];
  totals: {
    inbound_calls: number;
    inbound_minutes: number;
    outbound_calls: number;
    outbound_minutes: number;
    total_calls: number;
    total_minutes: number;
  };
}

export function QuickReportModal({ isOpen, onClose }: QuickReportModalProps) {
  const [period, setPeriod] = useState<Period>('1m');

  const { data, isLoading } = useQuery({
    queryKey: ['did-usage-report', period],
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/dids/usage-report/?period=${period}`);
      return response.data.data as UsageReportData;
    },
    enabled: isOpen,
  });

  const handleExportCSV = () => {
    if (!data) return;

    // Build CSV content
    const headers = [
      'DID',
      'Inbound Calls',
      'Inbound Minutes',
      'Outbound Calls',
      'Outbound Minutes',
      'Total Calls',
      'Total Minutes',
    ];
    const rows = data.dids.map((d) => [
      d.did,
      d.inbound_calls,
      d.inbound_minutes,
      d.outbound_calls,
      d.outbound_minutes,
      d.total_calls,
      d.total_minutes,
    ]);

    // Add totals row
    rows.push([
      'TOTALS',
      data.totals.inbound_calls,
      data.totals.inbound_minutes,
      data.totals.outbound_calls,
      data.totals.outbound_minutes,
      data.totals.total_calls,
      data.totals.total_minutes,
    ]);

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `did-usage-report-${period}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-900">DID Usage Report</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Period:</span>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {Object.entries(PERIOD_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleExportCSV}
            disabled={!data || isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-indigo-600 border-t-transparent rounded-full" />
            </div>
          ) : data ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">DID</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">
                    Inbound Calls
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">
                    Inbound Min
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">
                    Outbound Calls
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">
                    Outbound Min
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">
                    Total Calls
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">
                    Total Min
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.dids.map((row) => (
                  <tr
                    key={row.did}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="py-2 px-3 font-mono text-sm">
                      {formatPhoneNumber(row.did)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                      {row.inbound_calls.toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                      {row.inbound_minutes.toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                      {row.outbound_calls.toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                      {row.outbound_minutes.toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                      {row.total_calls.toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                      {row.total_minutes.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-medium">
                  <td className="py-2 px-3">TOTALS</td>
                  <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                    {data.totals.inbound_calls.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                    {data.totals.inbound_minutes.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                    {data.totals.outbound_calls.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                    {data.totals.outbound_minutes.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                    {data.totals.total_calls.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-sm tabular-nums">
                    {data.totals.total_minutes.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 text-xs text-slate-500">
          {data && (
            <span>
              Report period: {data.start_date} to {data.end_date} · {data.dids.length} DIDs
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
